/**
 * Animal Hierarchy Cache Service
 * 
 * Caches animal categories, subcategories, and breeds data in localStorage
 * since this data rarely changes. Provides a centralized cache that can be
 * used throughout the application.
 */
class AnimalHierarchyCache {
  constructor() {
    this.CACHE_KEY = 'animal_hierarchy_cache';
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.data = null;
    this.timestamp = null;
    this.pendingRequest = null; // Prevent multiple simultaneous requests
  }

  /**
   * Get cached data from memory or localStorage
   * @returns {Array|null} Cached hierarchy data or null if expired/missing
   */
  get() {
    // Check in-memory cache first
    if (this.data && this.timestamp && (Date.now() - this.timestamp) < this.CACHE_DURATION) {
      return this.data;
    }

    // Check localStorage
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < this.CACHE_DURATION) {
          this.data = parsed.data;
          this.timestamp = parsed.timestamp;
          return this.data;
        }
      }
    } catch (e) {
      console.error('Error reading hierarchy cache from localStorage:', e);
    }

    return null;
  }

  /**
   * Store data in both memory and localStorage
   * @param {Array} data - Hierarchy data to cache
   */
  set(data) {
    this.data = data;
    this.timestamp = Date.now();

    // Also store in localStorage for persistence across page refreshes
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify({
        data: data,
        timestamp: this.timestamp
      }));
    } catch (e) {
      console.error('Error storing hierarchy cache to localStorage:', e);
    }
  }

  /**
   * Clear cache from both memory and localStorage
   */
  clear() {
    this.data = null;
    this.timestamp = null;
    this.pendingRequest = null;
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (e) {
      console.error('Error clearing hierarchy cache from localStorage:', e);
    }
  }

  /**
   * Check if cache is valid (exists and not expired)
   * @returns {boolean}
   */
  isValid() {
    const cached = this.get();
    return cached !== null;
  }

  /**
   * Get cache age in milliseconds
   * @returns {number|null}
   */
  getAge() {
    if (!this.timestamp) {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          return parsed.timestamp ? Date.now() - parsed.timestamp : null;
        } catch (e) {
          return null;
        }
      }
      return null;
    }
    return Date.now() - this.timestamp;
  }

  /**
   * Force refresh the cache by clearing it
   * Next call to fetch() will fetch fresh data
   */
  invalidate() {
    this.clear();
  }

  /**
   * Fetch hierarchy data from API with caching
   * Prevents duplicate requests if one is already in progress
   * @param {Function} apiCall - Function that returns a promise for the API call
   * @returns {Promise<Array>} Hierarchy data
   */
  async fetch(apiCall) {
    // Check cache first
    const cached = this.get();
    if (cached) {
      return Promise.resolve(cached);
    }

    // If a request is already in progress, wait for it
    if (this.pendingRequest) {
      return this.pendingRequest;
    }

    // Make the API call
    this.pendingRequest = apiCall()
      .then(response => {
        const hierarchyData = response.data?.data || response.data || response;
        if (hierarchyData && hierarchyData.length > 0) {
          this.set(hierarchyData);
        }
        this.pendingRequest = null;
        return hierarchyData;
      })
      .catch(error => {
        this.pendingRequest = null;
        throw error;
      });

    return this.pendingRequest;
  }
}

// Export a singleton instance
const animalHierarchyCache = new AnimalHierarchyCache();

export default animalHierarchyCache;

